<script lang="ts" setup>
import { computed, reactive, ref, toRefs, watchEffect } from 'vue';
import type { ContentCollection } from '@ilteducation/content-collections-types';
import useLocale from '../composables/useLocale';
import type { CollectionFormPatch } from '../types';
import CollectionIcon from '../components/CollectionIcon.vue';
import useMyCollections from '../use-my-collections';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useDisplay } from 'vuetify';

export type Props = {
  collection?: ContentCollection;
  context: string;
  allAvailableIconGroups?: Record<string, string[]>;
  iconGroups?: string[];
  selectedIcon?: string;
  showIcons?: boolean;
  isOnline?: boolean;
  rounded: 'sm' | 'default' | 'lg' | 'xl';
};

const props = withDefaults(defineProps<Props>(), {
  allAvailableIconGroups: () => ({}),
  iconGroups: () => [],
  showIcons: true,
  isOnline: true,
});
const emit = defineEmits<{ (e: 'save', patch: CollectionFormPatch): void; (e: 'close'): void }>();
const { collection } = toRefs(props);
const { isLoading } = useMyCollections(props.context, 500);

const { t } = useLocale();
const { xs } = useDisplay();

const patch = ref<CollectionFormPatch>({ name: '', icon: props.selectedIcon || '' });

const validation = reactive({
  required: (value?: string) => !!value || '*',
  counter: (value?: string) => (value && value.length >= 2) || '*',
});

watchEffect(() => {
  patch.value = {
    name: collection.value?.name,
    icon: collection.value?.icon || props.selectedIcon || '',
  };
});

const selectedIconGroup = ref<string>('classics');

const filteredIcons = computed(() => {
  return props.allAvailableIconGroups[selectedIconGroup.value] || [];
});

const translatedIconGroups = computed(() => {
  return props.iconGroups.map((group: string) => {
    return {
      title: t(`icon_groups.${group}`),
      value: group,
    };
  });
});

const disableSave = computed(() => {
  if (!props.isOnline) return true;

  if (!patch.value.name || patch.value.name.length < 2) {
    return true;
  }

  if (filteredIcons.value.length > 0 && (!patch.value.icon || !filteredIcons.value?.includes(patch.value.icon))) {
    return true;
  }
  return isLoading.value;
});

const handleSave = () => emit('save', patch.value);
const handleCancel = () => emit('close');

const buttonRadius: Record<string, string> = {
  sm: 'xs',
  default: 'sm',
  lg: 'true',
  xl: 'lg',
};
</script>

<template>
  <div v-if="isLoading" class="d-flex justify-center pb-2">
    <v-progress-linear indeterminate color="accent" size="40" width="3"></v-progress-linear>
  </div>
  <v-card-text class="py-0">
    <v-row no-gutters>
      <v-col cols="12" align-self="center">
        <slot name="label_for_name"></slot>
        <v-text-field
          id="collection-name"
          v-model="patch.name"
          full-width
          class="mb-4"
          :rounded="buttonRadius[rounded]"
          :rules="[validation.required, validation.counter]"
          variant="outlined"
          single-line
          hide-details
          clearable
          aria-describedby="collection-name"
          :label="t('content_collections.label_collection_name')"
        />
      </v-col>
    </v-row>
    <v-row v-if="showIcons" no-gutters>
      <v-col cols="12">
        <v-row align-self="center" no-gutters class="justify-space-between mb-4">
          <v-col cols="auto" class="mr-4 pt-3">
            <slot name="label_for_icon">
              <span>{{ t('content_collections.label_choose_icon') }}</span>
            </slot>
          </v-col>

          <v-col cols="6">
            <v-select
              v-model="selectedIconGroup"
              :label="t('content_collections.label_choose_icon_group')"
              :items="translatedIconGroups"
              variant="outlined"
            ></v-select>
          </v-col>
        </v-row>

        <div class="mt-0 mx-auto mb-4">
          <v-btn-toggle
            id="collection-icon"
            v-model="patch.icon"
            color="accent"
            class="d-flex flex-row flex-wrap justify-center select-icon-group"
            mandatory
            aria-describedby="collection-icon"
          >
            <v-btn
              v-for="name in filteredIcons"
              :key="name"
              min-height="55"
              height="55"
              class="ma-0 bg-transparent"
              :value="name"
              :aria-label="t('content_collections.label_select_item', { name })"
            >
              <CollectionIcon :symbol="name" />
            </v-btn>
          </v-btn-toggle>
        </div>
      </v-col>
    </v-row>
  </v-card-text>
  <v-card-actions class="pa-6 d-flex ga-4">
    <v-btn
      min-width="120"
      :block="xs"
      color="secondary"
      variant="outlined"
      @click="handleCancel"
      :rounded="buttonRadius[rounded]"
    >
      <template #append>
        <FontAwesomeIcon icon="fa-solid fa-xmark" />
      </template>
      <span class="text-none">{{ t('button_cancel') }}</span>
    </v-btn>
    <v-btn
      :color="disableSave ? 'overline-variant' : 'primary'"
      :block="xs"
      min-width="120"
      variant="flat"
      :disabled="disableSave"
      :loading="isLoading"
      @click="handleSave"
      :rounded="buttonRadius[rounded]"
    >
      <template #append>
        <FontAwesomeIcon icon="fa-solid fa-check" />
      </template>
      <span class="text-none">{{ t('button_save') }}</span>
    </v-btn>
  </v-card-actions>
</template>

<style scoped lang="scss">
.select-icon-group {
  height: auto !important;
}

// For some reason the active btn overlay has opacity 1 from its calculations
// This is a fix for this issue.
:deep(.v-btn-toggle) {
  .v-btn--active {
    > .v-btn__overlay {
      opacity: 0 !important;
    }
  }
}
</style>
